import { pathSegments } from "../../../boundaries/url-path.parser";

export type LocalePathContext = {
  languageShells: ReadonlySet<string>;
};

const LANGUAGE_CODES = new Set([
  "ar",
  "cs",
  "da",
  "de",
  "el",
  "en",
  "es",
  "fi",
  "fr",
  "he",
  "hi",
  "hu",
  "id",
  "it",
  "ja",
  "ko",
  "ms",
  "nl",
  "no",
  "pl",
  "pt",
  "ro",
  "ru",
  "sk",
  "sv",
  "th",
  "tr",
  "uk",
  "vi",
  "zh",
]);
const REGION_CODES = new Set([
  "ae",
  "ar",
  "at",
  "au",
  "be",
  "br",
  "ca",
  "ch",
  "cl",
  "cn",
  "co",
  "cz",
  "de",
  "dk",
  "es",
  "fi",
  "fr",
  "gb",
  "gr",
  "hk",
  "hu",
  "id",
  "ie",
  "il",
  "in",
  "it",
  "jp",
  "kr",
  "mx",
  "my",
  "nl",
  "no",
  "nz",
  "pe",
  "ph",
  "pl",
  "pt",
  "ro",
  "ru",
  "se",
  "sg",
  "sk",
  "th",
  "tr",
  "tw",
  "ua",
  "uk",
  "us",
  "vn",
  "za",
]);

/** Identifies language-only path shells supported by parallel site structure. */
export function localePathContextForPages(
  pages: readonly { path: string }[],
): LocalePathContext {
  const candidates = new Map<
    string,
    { hasLanding: boolean; childSegments: Set<string> }
  >();
  for (const page of pages) {
    const segments = presentationPathSegments(page.path);
    const language = segments[0];
    if (!language || !LANGUAGE_CODES.has(language)) continue;
    if (isLanguageRegion(language, segments[1])) continue;
    const current = candidates.get(language) ?? {
      hasLanding: false,
      childSegments: new Set<string>(),
    };
    current.hasLanding ||= segments.length === 1;
    if (segments[1]) current.childSegments.add(segments[1]);
    candidates.set(language, current);
  }
  const parallelLanguages = [...candidates.entries()].flatMap(
    ([language, evidence]) => {
      if (!evidence.hasLanding || evidence.childSegments.size === 0) return [];
      const hasParallelSibling = [...candidates.entries()].some(
        ([otherLanguage, otherEvidence]) =>
          otherLanguage !== language &&
          otherEvidence.hasLanding &&
          [...evidence.childSegments].some((segment) =>
            otherEvidence.childSegments.has(segment),
          ),
      );
      return hasParallelSibling ? [language] : [];
    },
  );
  return { languageShells: new Set(parallelLanguages) };
}

/** Returns content segments after removing a supported locale prefix. */
export function localeContentPathSegments(
  path: string,
  context?: LocalePathContext,
): string[] {
  const segments = presentationPathSegments(path);
  return segments.slice(localePrefixLength(segments, context));
}

/** Returns stored route segments without presentation normalization. */
export function routePathSegments(path: string): string[] {
  return pathSegments(path);
}

function localePrefixLength(
  segments: string[],
  context?: LocalePathContext,
): number {
  const first = segments[0];
  if (!first) return 0;
  const hyphenated = first.split("-");
  if (
    hyphenated.length === 2 &&
    isLanguageRegion(hyphenated[0], hyphenated[1])
  ) {
    return 1;
  }
  if (isLanguageRegion(first, segments[1])) return 2;
  return context?.languageShells.has(first) ? 1 : 0;
}

function isLanguageRegion(
  language: string | undefined,
  region: string | undefined,
): boolean {
  return Boolean(
    language &&
      region &&
      LANGUAGE_CODES.has(language) &&
      REGION_CODES.has(region),
  );
}

function presentationPathSegments(path: string): string[] {
  return routePathSegments(path).map((segment) => segment.toLowerCase());
}
